/**
 * Client view of one monitored subject — where an alert email links to.
 *
 * Shows only the findings the team was actually sent, newest first. Read-only:
 * stopping monitoring is something the client asks GTI for, not a button here.
 */

import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Pagination,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft, IconExternalLink } from '@tabler/icons-react';

import { useClientMonitoredSubject } from '@/hooks/useClientMonitoring';
import {
  CLIENT_SUBJECT_STATUS_COLOURS,
  CLIENT_SUBJECT_STATUS_LABELS,
  findingCategoryLabel,
  formatMonitoringDate as formatDate,
  monitoringComponentLabel,
  safeExternalUrl,
  SentAlertRow,
  sentEventLabel,
  TeamSubjectRow,
} from '@/types/monitoring';

const PAGE_SIZE = 25;

const SEVERITY_COLOURS: Record<string, string> = {
  HIGH: 'red',
  MEDIUM: 'orange',
  LOW: 'gray',
  UNKNOWN: 'yellow',
};

/** Why nothing new is arriving, in the client's terms. */
const STATUS_NOTES: Partial<Record<TeamSubjectRow['status'], string>> = {
  pending: 'Monitoring starts once this subject is confirmed with our data provider.',
  suspended: 'Monitoring is paused. You will not receive new findings until it resumes.',
  cancelled: 'Monitoring has stopped. The findings below were sent while it was active.',
};

export function ClientMonitoredSubjectPage() {
  const { subjectId } = useParams<{ subjectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);

  const { detail, isLoading, error } = useClientMonitoredSubject(subjectId, {
    page,
    pageSize: PAGE_SIZE,
  });

  return (
    <Container size="lg" py="md">
      <Stack gap="md">
        <Anchor component={Link} to="/monitoring" size="sm">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            All monitored subjects
          </Group>
        </Anchor>

        {isLoading && !detail && (
          <Center h={300}>
            <Loader />
          </Center>
        )}

        {error && !detail && (
          <Center h={300}>
            <Text c="dimmed" data-testid="client-monitored-subject-error">
              {error.message}
            </Text>
          </Center>
        )}

        {detail && (
          <>
            <SubjectHeader subject={detail.subject} />

            {detail.alerts.length === 0 ? (
              <Card shadow="sm" padding="lg" radius="md">
                <Text c="dimmed">No findings have been sent about this subject.</Text>
              </Card>
            ) : (
              <Stack gap="sm">
                {detail.alerts.map((alert) => (
                  <FindingCard key={alert.id} alert={alert} />
                ))}
              </Stack>
            )}

            {detail.alertsTotal > PAGE_SIZE && (
              <Group justify="flex-end">
                <Pagination
                  value={page}
                  onChange={(p) => setSearchParams({ page: String(p) })}
                  total={Math.ceil(detail.alertsTotal / PAGE_SIZE)}
                  size="sm"
                />
              </Group>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

function SubjectHeader({ subject }: Readonly<{ subject: TeamSubjectRow }>) {
  const note = STATUS_NOTES[subject.status];
  return (
    <Stack gap={4}>
      <Group gap="sm">
        <Title order={2}>{subject.name}</Title>
        <Badge color={CLIENT_SUBJECT_STATUS_COLOURS[subject.status]} variant="light">
          {CLIENT_SUBJECT_STATUS_LABELS[subject.status]}
        </Badge>
      </Group>
      <Group gap="xs">
        {subject.scopeComponentSlugs.map((slug) => (
          <Badge key={slug} size="sm" variant="outline" color="gray">
            {monitoringComponentLabel(slug)}
          </Badge>
        ))}
        <Text size="sm" c="dimmed">
          Monitored since {formatDate(subject.enrolledAt)}
        </Text>
        {subject.reportId && (
          <Anchor component={Link} to={`/reports/${subject.reportId}`} size="sm">
            View report
          </Anchor>
        )}
      </Group>
      {note && (
        <Text size="sm" c="dimmed" data-testid="client-monitored-subject-note">
          {note}
        </Text>
      )}
    </Stack>
  );
}

function FindingCard({ alert }: Readonly<{ alert: SentAlertRow }>) {
  const { entry } = alert;
  const href = safeExternalUrl(entry.url);
  return (
    <Card shadow="sm" padding="md" radius="md" data-testid={`sent-alert-${alert.id}`}>
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs">
            <Badge variant="light">{sentEventLabel(alert.lastSentEvent)}</Badge>
            {alert.severity && (
              <Badge color={SEVERITY_COLOURS[alert.severity] ?? 'gray'} variant="light">
                {alert.severity.toLowerCase()}
              </Badge>
            )}
            <Text size="sm" fw={500}>
              {findingCategoryLabel(alert.taxonomyCategory)}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Sent {formatDate(alert.lastSentAt)}
          </Text>
        </Group>

        <Text size="sm">
          {monitoringComponentLabel(alert.scopeComponentSlug)}
          {entry.listSource ? ` · ${entry.listSource}` : ''}
          {entry.name ? ` · ${entry.name}` : ''}
        </Text>

        {entry.title && (
          <Group gap={4} wrap="nowrap">
            {href ? (
              <Anchor href={href} target="_blank" rel="noreferrer" size="sm">
                <Group gap={4} wrap="nowrap">
                  {entry.title}
                  <IconExternalLink size={12} />
                </Group>
              </Anchor>
            ) : (
              <Text size="sm">{entry.title}</Text>
            )}
            {entry.publishedDate && (
              <Text size="xs" c="dimmed">
                ({formatDate(entry.publishedDate)})
              </Text>
            )}
          </Group>
        )}

        {alert.firstSentAt && alert.firstSentAt !== alert.lastSentAt && (
          <Text size="xs" c="dimmed">
            First sent {formatDate(alert.firstSentAt)}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
